import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Droplet, Clock } from "lucide-react";

/**
 * Olive-oil-specific freshness signal. Olive oil shoppers obsess over the
 * pressing-to-harvest gap — under 8h is gold-standard, 9-24h is good, past 24h
 * is acceptable but worth signalling honestly. We pick the right tier so we
 * never overstate, and we hide entirely when the producer didn't supply data.
 *
 * `compact=true` is for product cards (single inline line).
 * `compact=false` is for ProductDetail (icon + label + supporting line).
 */
export const HarvestFreshness = ({ hoursToPress, harvestDate, compact = false }) => {
  const { t, i18n } = useTranslation();
  if (hoursToPress == null) return null;

  const tier = hoursToPress <= 8 ? "gold" : hoursToPress <= 24 ? "good" : "ok";
  const Icon = tier === "gold" ? Sparkles : tier === "good" ? Droplet : Clock;

  // Tier classes — gold is loud (olive bg), good is soft (olive text on bone),
  // ok is muted (ink-soft).
  const palette = {
    gold: compact ? "text-olive font-medium" : "text-olive",
    good: compact ? "text-olive" : "text-olive",
    ok: "text-ink-soft",
  }[tier];

  const label = (() => {
    if (tier === "gold") return t("freshness.gold", { hours: hoursToPress });
    if (tier === "good") return t("freshness.good", { hours: hoursToPress });
    return t("freshness.ok", { hours: hoursToPress });
  })();

  // Harvest date — short locale format. e.g. "Nov 12, 2025" / "12. stu 2025."
  const harvestPretty = harvestDate
    ? new Date(harvestDate).toLocaleDateString(i18n.language.startsWith("hr") ? "hr-HR" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  if (compact) {
    return (
      <p
        className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest mt-3 ${palette}`}
        data-testid="freshness-compact"
        data-tier={tier}
      >
        <Icon className="w-3 h-3" />
        <span>{label}</span>
      </p>
    );
  }

  return (
    <div
      className={`flex items-start gap-3 mt-6 p-4 rounded-sm hairline bg-bone-alt ${tier === "gold" ? "border-olive/30 bg-olive/5" : ""}`}
      data-testid="freshness-detail"
      data-tier={tier}
    >
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${palette}`} />
      <div>
        <p className={`font-medium ${palette}`}>{label}</p>
        {harvestPretty && (
          <p className="text-xs text-ink-muted mt-1">{t("freshness.harvestedOn", { date: harvestPretty })}</p>
        )}
      </div>
    </div>
  );
};

export default HarvestFreshness;
